// ./sections/SignUpTabs.jsx
import React from "react";
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  InputAdornment,
  Typography,
  Stack,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";

import { supabase } from "../../../components/SupabaseClient";

import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import KeyIcon from "@mui/icons-material/Key";

import AuthField from "../components/AuthField.jsx";
import FormQuestion from "../components/SignUpFormQuestion";
import PasswordRulesDisplay from "../components/PasswordRulesDisplay.jsx";
import { fieldSx, primaryBtnSx } from "../components/authStyles";
import {
  checkUserExists,
  isValidEmail,
  runPasswordRules,
} from "../../../utils/userCredentialValidation";
import { useNavigate } from "react-router-dom";
import { OCCUPATION_OPTIONS as ROLE_OPTIONS } from "../../../constants/userOptions";

import { getProfileFieldError } from "../../../utils/userProfileValidation";

const STEPS = ["Information & Affiliation", "Account Credentials"];

// Color used for the read-only username field; brand/border colors now come
// from the shared MUI theme + authStyles.
const READONLY_TEXT = "#475569";

const SIGNUP_REDIRECT_URL = `${window.location.origin}/signin`;

export default function SignUpTabs() {
  const [tab, setTab] = React.useState(0);
  const [showPwd, setShowPwd] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);

  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [affiliation, setAffiliation] = React.useState("");
  const [role, setRole] = React.useState("");
  const [emailValue, setEmailValue] = React.useState("");

  const [passwordValue, setPasswordValue] = React.useState("");
  const [confirmValue, setConfirmValue] = React.useState("");
  const [passwordError, setPasswordError] = React.useState("");
  const [confirmError, setConfirmError] = React.useState("");
  const [submitError, setSubmitError] = React.useState("");

  const [emailExists, setEmailExists] = React.useState(false);
  const [emailError, setEmailError] = React.useState("");

  const [openDialog, setOpenDialog] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [checkingEmail, setCheckingEmail] = React.useState(false);

  const navigate = useNavigate();
  const sanitizedEmailValue = emailValue.trim().toLowerCase();

  const fieldErrors = {
    first_name: getProfileFieldError("first_name", firstName),
    last_name: getProfileFieldError("last_name", lastName),
    institution: getProfileFieldError("institution", affiliation),
    occupation: getProfileFieldError("occupation", role),
  };
  const hasFieldErrors = Object.values(fieldErrors).some(Boolean);

  const canProceed =
    firstName.trim() &&
    lastName.trim() &&
    affiliation.trim() &&
    role.trim() &&
    sanitizedEmailValue &&
    !emailError &&
    !hasFieldErrors;

  const passwordRulesState = runPasswordRules(passwordValue);

  const handleNextClick = async () => {
    if (!canProceed) return;

    if (!isValidEmail(emailValue)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    setEmailExists(false);
    setEmailError("");
    setCheckingEmail(true);

    const email = sanitizedEmailValue;
    const { exists: alreadyExists, error: userLookupError } =
      await checkUserExists(email);

    if (userLookupError) {
      setCheckingEmail(false);
      setEmailError("Unable to verify this email right now. Please try again.");
      return;
    }

    if (alreadyExists) {
      setEmailExists(true);
      setCheckingEmail(false);
      return;
    }

    setEmailExists(false);
    setEmailError("");
    setCheckingEmail(false);
    setTab(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!canProceed) {
      setTab(0);
      return;
    }
    setLoading(true);

    const firstNameVal = firstName.trim();
    const lastNameVal = lastName.trim();
    const email = sanitizedEmailValue;
    const password = passwordValue;
    const confirmPassword = confirmValue;
    const affiliationVal = affiliation.trim();
    const roleVal = role.trim();

    const rules = runPasswordRules(password);
    const firstFailed = rules.find((r) => !r.valid);
    const confirmErr =
      password !== confirmPassword ? "Passwords do not match." : "";

    setPasswordError(firstFailed ? firstFailed.message : "");
    setConfirmError(confirmErr);

    if (firstFailed || confirmErr) {
      setTab(1);
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: SIGNUP_REDIRECT_URL,
          data: {
            first_name: firstNameVal,
            last_name: lastNameVal,
            institution: affiliationVal,
            occupation: roleVal,
          },
        },
      });

      if (error) {
        setSubmitError(error.message || "Failed to sign up.");
        return;
      }

      setOpenDialog(true);
    } catch (err) {
      setSubmitError(err.message || "Failed to sign up.");
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    navigate("/");
  };

  return (
    <Box component="form" noValidate onSubmit={handleSubmit}>
      <Stepper activeStep={tab} alternativeLabel sx={{ mb: 1 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 1 */}
      <Box hidden={tab !== 0} sx={{ mt: 3 }}>
        <Stack spacing={3}>
          <FormQuestion>Please enter your first and last name.</FormQuestion>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              fullWidth
              required
              placeholder="First name"
              name="firstName"
              value={firstName}
              error={Boolean(fieldErrors.first_name)}
              helperText={fieldErrors.first_name}
              onChange={(e) => setFirstName(e.target.value)}
              sx={fieldSx}
              slotProps={{
                formHelperText: { sx: { whiteSpace: "nowrap" } },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <BadgeOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              fullWidth
              required
              placeholder="Last name"
              name="lastName"
              value={lastName}
              error={Boolean(fieldErrors.last_name)}
              helperText={fieldErrors.last_name}
              onChange={(e) => setLastName(e.target.value)}
              sx={fieldSx}
              slotProps={{
                formHelperText: { sx: { whiteSpace: "nowrap" } },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <BadgeOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Stack>

          <FormQuestion>Please enter your affiliation.</FormQuestion>
          <TextField
            fullWidth
            required
            name="affiliation"
            value={affiliation}
            error={Boolean(fieldErrors.institution)}
            helperText={fieldErrors.institution}
            onChange={(e) => setAffiliation(e.target.value)}
            placeholder="Your university, lab, company, or organization"
            sx={fieldSx}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <ApartmentOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />

          <Typography variant="body2" color="text.secondary">
            You can add department and advisor information later in your
            profile.
          </Typography>

          <FormQuestion>What best describes you?</FormQuestion>
          <TextField
            select
            fullWidth
            required
            name="role"
            value={role}
            error={Boolean(fieldErrors.occupation)}
            helperText={fieldErrors.occupation}
            onChange={(e) => setRole(e.target.value)}
            sx={fieldSx}
          >
            {ROLE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>

          <FormQuestion>Please enter your email.</FormQuestion>
          <Box>
            <TextField
              fullWidth
              required
              type="email"
              placeholder="Enter your email"
              name="email"
              value={emailValue}
              error={emailExists || !!emailError}
              onChange={(e) => {
                setEmailValue(e.target.value);
                setEmailExists(false);
                setEmailError("");
              }}
              onBlur={() => {
                const v = emailValue.trim();
                setEmailError(
                  v && !isValidEmail(emailValue)
                    ? "Please enter a valid email address."
                    : "",
                );
              }}
              // Plain email-format errors go in helperText; account guidance
              // renders below with reserved space.
              helperText={emailExists ? " " : emailError || " "}
              sx={fieldSx}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlineIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />

            {emailExists ? (
              <Typography
                sx={{
                  color: "error.main",
                  mt: -1.5,
                  fontSize: "0.8rem",
                  lineHeight: 1.4,
                }}
              >
                If you&apos;ve used this email before, please{" "}
                <Typography
                  component="span"
                  color="primary"
                  sx={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: "0.8rem",
                  }}
                  onClick={() => navigate("/signin")}
                >
                  sign in
                </Typography>{" "}
                or{" "}
                <Typography
                  component="span"
                  color="primary"
                  sx={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: "0.8rem",
                  }}
                  onClick={() => navigate("/forgot-password")}
                >
                  reset your password
                </Typography>
                . Otherwise, try a different email.
              </Typography>
            ) : null}
          </Box>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="button"
              onClick={handleNextClick}
              disabled={!canProceed || checkingEmail}
              sx={{ ...primaryBtnSx, mt: 0, minWidth: 100 }}
            >
              {checkingEmail ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                "Next"
              )}
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* Step 2 */}
      <Box hidden={tab !== 1} sx={{ mt: 3 }}>
        <Stack spacing={3}>
          {submitError && <Typography color="error">{submitError}</Typography>}

          <FormQuestion>Username</FormQuestion>
          <TextField
            fullWidth
            value={sanitizedEmailValue}
            slotProps={{
              input: {
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineIcon fontSize="small" />
                  </InputAdornment>
                ),
                sx: {
                  color: READONLY_TEXT, // muted: this field is read-only
                },
              },
            }}
            sx={fieldSx}
          />

          <FormQuestion>Set your password.</FormQuestion>
          <AuthField
            name="password"
            required
            placeholder="Enter your password"
            value={passwordValue}
            onChange={(e) => {
              const val = e.target.value;
              setPasswordValue(val);

              const rules = runPasswordRules(val);
              const firstFailed = rules.find((r) => !r.valid);
              setPasswordError(firstFailed ? firstFailed.message : "");

              // Re-validate confirm so fixing the password clears a stale
              // "Passwords do not match" error.
              if (confirmValue) {
                setConfirmError(
                  val !== confirmValue ? "Passwords do not match." : "",
                );
              }
            }}
            error={passwordError}
            startIcon={<KeyIcon fontSize="small" />}
            password
            showPassword={showPwd}
            onTogglePassword={() => setShowPwd((v) => !v)}
          />

          <PasswordRulesDisplay rules={passwordRulesState} />

          <FormQuestion>Confirm your password.</FormQuestion>
          <AuthField
            name="confirmPassword"
            required
            placeholder="Re-enter your password"
            value={confirmValue}
            onChange={(e) => {
              const confirmVal = e.target.value;
              setConfirmValue(confirmVal);
              setConfirmError(
                passwordValue !== confirmVal ? "Passwords do not match." : "",
              );
            }}
            error={confirmError}
            startIcon={<KeyIcon fontSize="small" />}
            password
            showPassword={showConfirm}
            onTogglePassword={() => setShowConfirm((v) => !v)}
            toggleAriaLabel="toggle confirm password visibility"
          />

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              type="button"
              onClick={() => setTab(0)}
              variant="outlined"
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Back
            </Button>
            <Button
              type="submit"
              disabled={loading || !canProceed}
              sx={{ ...primaryBtnSx, mt: 0, minWidth: 120 }}
              startIcon={
                loading ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : null
              }
            >
              {loading ? "Submitting…" : "Submit"}
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* success dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>Email Verification</DialogTitle>
        <DialogContent>
          <Typography>
            A confirmation email has been sent to your email. Please open the
            link in the email to complete your registration.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDialogClose}
            autoFocus
            variant="outlinedButton"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
